import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Toaster } from '../components/ui/toaster';
import { VondiLogo } from '../components/ui/VondiLogo';
import { GlassSurface } from '../components/ui/GlassSurface';
import CajaScanner from '../components/caja/CajaScanner';
import { cajaService, cajaSesion } from '../services/caja';

/*
  La caja de una tienda: `/t/{slug}`.

  Reemplaza a `/scan`, que era una pantalla pública con un desplegable de
  tiendas. Aquel desplegable no era un detalle de forma: un cajero de Nike
  podía elegir Starbucks por error y ese canje quedaba mal atribuido para
  siempre — contaminaba el ahorro del cliente, el reporte de las dos tiendas y
  las cifras de plataforma, sin que nadie lo notara hasta revisar un reporte.

  Aquí la tienda no se elige: es la del enlace, y el servidor la confirma. El
  PIN es lo único que se teclea, y es corto a propósito porque **el cajero lo
  va a apuntar en un papel pegado al monitor** — pasa en todos los comercios.
  Por eso no es la contraseña de la cuenta: solo sirve en esta URL, solo para
  canjear, y se rota desde el admin sin tocar la cuenta del dueño.

  Formato (BRAND.md): la pantalla tiene dos caras deliberadamente distintas.

    · **La puerta** va sobre el degradado de marca, con el vidrio del manual.
      Es un instante y es la única vez que esta pantalla se presenta: la tienda
      tiene que reconocer de quién es esto antes de teclear su clave.
    · **La caja abierta** va sobre lienzo claro. Lo manda la regla 60/30/10 —el
      color es acento, nunca satura pantallas completas— y lo pide el uso: es
      la herramienta que el cajero mira todo el día bajo la luz del mostrador,
      no una pantalla de campaña.
*/
const Caja = () => {
  const { slug } = useParams();

  const [cargando, setCargando] = useState(true);
  const [tienda, setTienda] = useState(null);
  const [errorTienda, setErrorTienda] = useState(null);
  const [autenticada, setAutenticada] = useState(false);

  const [pin, setPin] = useState('');
  const [entrando, setEntrando] = useState(false);
  const [errorPin, setErrorPin] = useState(null);

  /*
    Al abrir se hacen dos cosas: pedir la marca de la tienda (para que el
    cajero vea de un vistazo que entró por el enlace correcto antes de teclear
    nada) y comprobar contra el servidor si la sesión guardada sigue viva. Lo
    segundo es lo que evita descubrir que el turno venció justo cuando ya hay
    un cliente esperando enfrente.
  */
  const arrancar = useCallback(async () => {
    setCargando(true);
    setErrorTienda(null);

    try {
      const info = await cajaService.tienda(slug);
      setTienda(info.data);

      if (cajaSesion.leer(slug)) {
        try {
          await cajaService.sesion(slug);
          setAutenticada(true);
        } catch (e) {
          cajaSesion.borrar(slug);
          setAutenticada(false);
        }
      }
    } catch (err) {
      setErrorTienda(err.message || 'Este enlace no corresponde a ninguna tienda activa.');
    } finally {
      setCargando(false);
    }
  }, [slug]);

  useEffect(() => {
    arrancar();
  }, [arrancar]);

  const entrar = async (e) => {
    e?.preventDefault();
    setErrorPin(null);
    setEntrando(true);

    try {
      const res = await cajaService.entrar(slug, pin.trim());
      cajaSesion.guardar(slug, res.data.token, res.data.expires_at);
      setTienda((t) => ({ ...(t || {}), ...(res.data.store || {}) }));
      setPin('');
      setAutenticada(true);
    } catch (err) {
      setErrorPin(err.message || 'No se pudo abrir la caja.');
    } finally {
      setEntrando(false);
    }
  };

  const cerrar = () => {
    cajaSesion.borrar(slug);
    setAutenticada(false);
  };

  /* ── Cargando ── */
  if (cargando) {
    return (
      <div className="min-h-screen-safe bg-grad-marca flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  /* ── Enlace que no lleva a ninguna parte ── */
  if (errorTienda) {
    return (
      <div className="min-h-screen-safe bg-grad-marca flex flex-col items-center justify-center px-6 py-16">
        <VondiLogo variant="blanco" size={26} />
        <GlassSurface className="w-full max-w-sm mt-8 p-8 text-center">
          <h1 className="font-display text-vd-h3 text-vd-tinta">Este enlace no funciona</h1>
          <p className="text-vd-body text-vd-pizarra mt-3">{errorTienda}</p>
          <p className="text-sm text-vd-pizarra mt-4">
            Pídele a vondi el enlace de caja de tu tienda. Cada tienda tiene el suyo.
          </p>
        </GlassSurface>
      </div>
    );
  }

  /* Identidad de la tienda, sobre vidrio. El logo del aliado va en su pastilla
     blanca porque casi siempre es una imagen a todo color y necesita fondo. */
  const identidadTienda = (
    <div className="flex items-center gap-3">
      {tienda?.logo && (
        <div className="w-12 h-12 rounded-input bg-white overflow-hidden shrink-0 shadow-warm-sm">
          <img src={tienda.logo} alt={tienda.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="text-left">
        <p className="text-vd-label uppercase text-vd-pizarra">Caja de</p>
        <h1 className="font-display text-vd-h3 text-vd-tinta leading-tight">{tienda?.name}</h1>
      </div>
    </div>
  );

  /* ── La puerta ── */
  if (!autenticada) {
    return (
      <div className="min-h-screen-safe bg-grad-marca flex flex-col items-center px-6 pt-safe-top pb-16">
        <Toaster />

        <div className="mt-14">
          <VondiLogo variant="blanco" size={34} withDescriptor />
        </div>

        <GlassSurface className="w-full max-w-sm mt-10 p-7">
          <div className="flex justify-center pb-6 border-b border-vd-linea/70">{identidadTienda}</div>

          {tienda?.pin_configurado === false ? (
            /* Enlace correcto, puerta sin llave todavía. Se dice explícito para
               que el cajero no se quede tecleando contra algo que no abre. */
            <div className="pt-6 text-center">
              <div className="w-12 h-12 rounded-full bg-vd-ambar/15 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-5 h-5 text-vd-ambar" />
              </div>
              <p className="font-semibold text-vd-tinta">Esta tienda todavía no tiene PIN</p>
              <p className="text-sm text-vd-pizarra mt-2">
                Pídele a vondi que genere el PIN de caja de {tienda?.name}.
              </p>
            </div>
          ) : (
            <form onSubmit={entrar} className="pt-6 space-y-5">
              <div className="text-center">
                <p className="font-display text-vd-h3 text-vd-tinta">Escribe tu PIN</p>
                <p className="text-sm text-vd-pizarra mt-1">Se pide una vez por turno en este equipo.</p>
              </div>

              <Input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="••••••"
                className="h-14 rounded-input border-vd-linea bg-white text-center font-display text-2xl tracking-[0.5em] text-vd-tinta shadow-none"
                disabled={entrando}
              />

              {errorPin && <p className="text-sm text-vd-alerta text-center">{errorPin}</p>}

              <Button
                type="submit"
                variant="vondi"
                size="pill"
                disabled={pin.length < 4 || entrando}
                className="w-full"
              >
                {entrando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Abriendo...
                  </>
                ) : (
                  'Abrir caja'
                )}
              </Button>
            </form>
          )}
        </GlassSurface>

        <p className="text-white/80 text-sm mt-8 text-center max-w-xs">
          Con vondi, tus clientes pagan menos. Aquí aplicas su descuento en segundos.
        </p>
      </div>
    );
  }

  /* ── La caja abierta ── */
  return (
    <div className="min-h-screen-safe bg-vd-niebla pb-16">
      <Toaster />

      {/* Barra de la tienda. Se queda arriba mientras se trabaja: es el recordatorio
          permanente de a nombre de quién se está canjeando. */}
      <header className="bg-white border-b border-vd-linea sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          {identidadTienda}
          <div className="flex flex-col items-end gap-2 shrink-0">
            <VondiLogo variant="gradiente" size={15} />
            <button
              onClick={cerrar}
              className="text-vd-pizarra hover:text-vd-tinta text-xs flex items-center gap-1 transition-colors"
              title="Cerrar la caja en este equipo"
            >
              <LogOut className="w-3.5 h-3.5" />
              Cerrar caja
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-5">
        <CajaScanner
          slug={slug}
          storeName={tienda?.name}
          onSesionInvalida={() => {
            cajaSesion.borrar(slug);
            setAutenticada(false);
            setErrorPin('Tu turno terminó. Escribe el PIN otra vez.');
          }}
        />
      </main>
    </div>
  );
};

export default Caja;
